/**
 * AgentsSection - Settings panel for managed-agent administration.
 *
 * A managed agent bundles a provider, model, standing instructions, an
 * allowlist of tools, team visibility, and subagents it may delegate to (see
 * `ManagedAgent` in @taskin-code/contracts). Selecting one in a thread's model
 * picker applies the bundle (a later phase); this panel only edits the
 * definitions.
 *
 * Follows TeamsSection's pattern: manual `refresh()` + useState, gated on
 * `a.view` to view and `a.edit` to mutate, `window.confirm` for delete. Two
 * gates apply here, not one — this component self-protects on `a.view` the
 * same way TeamsSection does on `u.view`, and `Sidebar.tsx` separately hides
 * the nav entry on the same permission so a caller without it never lands
 * here at all; either gate alone would leave a way in.
 *
 * The team multi-select needs the admin team roster (`TeamsClient.list()`,
 * `u.view`-gated), which an agent editor does not necessarily hold. When that
 * fetch fails, the dialog falls back to showing the agent's current teamIds
 * as raw, read-only ids rather than hiding the field or guessing names —
 * see `TeamsSection`'s own comment on why membership dialogs use the admin
 * roster instead of the visibility-filtered directory.
 */
import type {
  ManagedAgent,
  ManagedAgentId,
  ManagedAgentMode,
  ProviderKind,
  Tag,
  TeamId,
  TeamWithMembers,
} from "@taskin-code/contracts";
import {
  getManagedAgentToolOptions,
  MODEL_OPTIONS_BY_PROVIDER,
  PROVIDER_DISPLAY_NAMES,
} from "@taskin-code/contracts";
import {
  canRunSideChat,
  describeManagedAgentMode,
  MANAGED_AGENT_MODE_LABELS,
  MANAGED_AGENT_MODE_OPTIONS,
} from "@taskin-code/shared/managedAgentMode";
import { useEffect, useMemo, useRef, useState } from "react";

import { AgentsClient } from "../../agents/AgentsClient";
import { useAuthStore } from "../../auth/authStore";
import { readFileAsDataUrl } from "../../lib/readFileAsDataUrl";
import { AgentAvatar } from "../AgentAvatar";
import { TeamsClient } from "../../auth/TeamsClient";
import { TagsClient } from "../../tags/TagsClient";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  agentToFormState,
  buildCreateRequest,
  CODEX_QA_SHELL_WARNING,
  buildUpdateRequest,
  EMPTY_AGENT_FORM,
  filterSubagentCandidates,
  filterToolsForProvider,
  groupToolOptionsBySection,
  agentEffortChoices,
  describeAgentTraits,
  resolveModelForProviderChange,
  resolveTraitsForModelChange,
  resolveNamesById,
  sortAgentsByName,
  validateAgentName,
  warnsAboutCodexShellForQa,
  type AgentFormState,
} from "./AgentsSection.logic";
import { Field, ModalShell } from "./settingsModal";
import { TagPicker } from "./TagPicker";
import { AVATAR_FILE_INPUT_ACCEPT, validateAvatarFile } from "./ProfileSection.logic";

const PROVIDER_OPTIONS: ReadonlyArray<ProviderKind> = ["claudeAgent", "codex"];

export function AgentsSection() {
  const hasView = useAuthStore((state) => state.globalPermissions.has("a.view"));
  const hasEdit = useAuthStore((state) => state.globalPermissions.has("a.edit"));
  const [agents, setAgents] = useState<ReadonlyArray<ManagedAgent>>([]);
  const [teams, setTeams] = useState<ReadonlyArray<TeamWithMembers>>([]);
  const [teamsLoadFailed, setTeamsLoadFailed] = useState(false);
  /**
   * The tag vocabulary for the editor's picker. Failures are swallowed: an
   * admin without `t.view` can still edit agents, they simply cannot tag them,
   * and an error about a screen they were never promised would be noise.
   */
  const [availableTags, setAvailableTags] = useState<ReadonlyArray<Tag>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingAgentId, setEditingAgentId] = useState<ManagedAgentId | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const agentsResult = await AgentsClient.list();
      setAgents(agentsResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load agents");
    } finally {
      setLoading(false);
    }
    try {
      const teamsResult = await TeamsClient.list();
      setTeams(teamsResult);
      setTeamsLoadFailed(false);
    } catch {
      // Not every a.view holder also holds u.view. The dialog falls back to
      // raw teamIds when this happens — see the file header.
      setTeams([]);
      setTeamsLoadFailed(true);
    }
    try {
      const tagEntries = await TagsClient.list();
      setAvailableTags(tagEntries.map((entry) => entry.tag));
    } catch {
      setAvailableTags([]);
    }
  };

  useEffect(() => {
    if (!hasView) return;
    void refresh();
  }, [hasView]);

  if (!hasView) {
    return (
      <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
        You need the <code>a.view</code> permission to manage agents.
      </div>
    );
  }

  const sortedAgents = sortAgentsByName(agents);
  const editingAgent = agents.find((a) => a.id === editingAgentId) ?? null;
  const agentNameLookup = agents.map((a) => ({ id: a.id, name: a.name }));
  const teamNameLookup = teams.map((t) => ({ id: t.id, name: t.name }));

  const removeAgent = async (agent: ManagedAgent) => {
    if (!window.confirm(`Delete agent "${agent.name}"? This cannot be undone.`)) {
      return;
    }
    setError(null);
    try {
      await AgentsClient.remove(agent.id);
      void refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete agent");
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold">Agents</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Define custom agents with their own model, instructions, tools, and delegation settings.
          </p>
        </div>
        <Button
          size="sm"
          className="shrink-0"
          disabled={!hasEdit}
          onClick={() => setCreateOpen(true)}
        >
          + New agent
        </Button>
      </div>

      {error ? (
        <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          {error}
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-border/60 bg-card/40">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="border-b border-border/40 bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="w-[20%] px-4 py-2 font-medium">Agent</th>
              <th className="w-[9%] px-4 py-2 font-medium">Mode</th>
              <th className="w-[18%] px-4 py-2 font-medium">Provider / model</th>
              <th className="w-[18%] px-4 py-2 font-medium">Teams</th>
              <th className="px-4 py-2 font-medium">Subagents</th>
              <th className="w-[1%] px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && sortedAgents.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-xs text-muted-foreground">
                  Loading agents…
                </td>
              </tr>
            ) : sortedAgents.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-xs text-muted-foreground">
                  No agents yet.
                </td>
              </tr>
            ) : (
              sortedAgents.map((agent) => {
                const teamNames = teamsLoadFailed
                  ? agent.teamIds
                  : resolveNamesById(agent.teamIds, teamNameLookup);
                const subagentNames = resolveNamesById(agent.subagentIds, agentNameLookup);
                return (
                  <tr
                    key={agent.id}
                    className="border-b border-border/30 align-top last:border-b-0"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium">{agent.name}</div>
                      {agent.description ? (
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {agent.description}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <AgentModeBadge mode={agent.mode} />
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      <div>{PROVIDER_DISPLAY_NAMES[agent.provider]}</div>
                      <div className="font-mono">{agent.model}</div>
                      <div className="mt-0.5">{describeAgentTraits(agent)}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {teamNames.length === 0 ? "Everyone" : teamNames.join(", ")}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {subagentNames.length === 0 ? "—" : subagentNames.join(", ")}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!hasEdit}
                        onClick={() => setEditingAgentId(agent.id)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-2 text-red-500 hover:text-red-400"
                        disabled={!hasEdit}
                        onClick={() => void removeAgent(agent)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {createOpen ? (
        <AgentEditorDialog
          title="Create agent"
          submitLabel="Create"
          initialForm={EMPTY_AGENT_FORM}
          agents={agents}
          excludeAgentId={null}
          teams={teams}
          teamsLoadFailed={teamsLoadFailed}
          availableTags={availableTags}
          agentId={null}
          initialAvatarFileName={null}
          onClose={() => setCreateOpen(false)}
          onSubmit={async (form) => {
            await AgentsClient.create(buildCreateRequest(form));
          }}
          onDone={() => {
            setCreateOpen(false);
            void refresh();
          }}
        />
      ) : null}

      {editingAgent ? (
        <AgentEditorDialog
          title={`Edit "${editingAgent.name}"`}
          submitLabel="Save"
          initialForm={agentToFormState(editingAgent)}
          agents={agents}
          excludeAgentId={editingAgent.id}
          teams={teams}
          teamsLoadFailed={teamsLoadFailed}
          availableTags={availableTags}
          agentId={editingAgent.id}
          initialAvatarFileName={editingAgent.avatarFileName}
          onClose={() => setEditingAgentId(null)}
          onSubmit={async (form) => {
            await AgentsClient.update(editingAgent.id, buildUpdateRequest(form));
          }}
          onDone={() => {
            setEditingAgentId(null);
            void refresh();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The mode as a chip in the roster.
 *
 * `general` is deliberately the quiet one: it is the default and the majority,
 * so styling it like the two deliberate choices would make the column read as
 * noise instead of as "these two agents were narrowed on purpose".
 */
function AgentModeBadge(props: { mode: ManagedAgentMode }) {
  const toneClassName =
    props.mode === "subagent"
      ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
      : props.mode === "primary"
        ? "border-sky-500/40 bg-sky-500/10 text-sky-300"
        : props.mode === "coordinator"
          ? "border-violet-500/40 bg-violet-500/10 text-violet-300"
          : "border-border/50 bg-muted/30 text-muted-foreground";
  return (
    <span
      className={`inline-block whitespace-nowrap rounded border px-1.5 py-0.5 text-[11px] ${toneClassName}`}
      title={describeManagedAgentMode(props.mode)}
    >
      {MANAGED_AGENT_MODE_LABELS[props.mode]}
    </span>
  );
}

function AgentEditorDialog(props: {
  title: string;
  submitLabel: string;
  initialForm: AgentFormState;
  agents: ReadonlyArray<ManagedAgent>;
  excludeAgentId: ManagedAgentId | null;
  teams: ReadonlyArray<TeamWithMembers>;
  teamsLoadFailed: boolean;
  availableTags: ReadonlyArray<Tag>;
  agentId: ManagedAgentId | null;
  initialAvatarFileName: string | null;
  onClose: () => void;
  onSubmit: (form: AgentFormState) => Promise<void>;
  onDone: () => void;
}) {
  const [form, setForm] = useState<AgentFormState>(props.initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avatarFileName, setAvatarFileName] = useState(props.initialAvatarFileName);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const avatarFileInputRef = useRef<HTMLInputElement>(null);

  const availableTags = props.availableTags;
  const toolOptions = useMemo(() => getManagedAgentToolOptions(form.provider), [form.provider]);
  const toolSections = useMemo(() => groupToolOptionsBySection(toolOptions), [toolOptions]);
  const modelOptions = MODEL_OPTIONS_BY_PROVIDER[form.provider];
  const subagentCandidates = filterSubagentCandidates({
    agents: props.agents,
    excludeAgentId: props.excludeAgentId,
    selectedSubagentIds: form.subagentIds,
  });
  // A subagent-only agent is never offered in a picker, so a side chat is not on
  // the table for it either — see `canRunSideChat`.
  const sideChatAvailable = canRunSideChat(form.mode);

  useEffect(() => {
    setAvatarFileName(props.initialAvatarFileName);
  }, [props.initialAvatarFileName]);

  const runAvatar = async (action: () => Promise<string | null>) => {
    if (avatarBusy || props.agentId === null) return;
    setAvatarBusy(true);
    setError(null);
    try {
      setAvatarFileName(await action());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update photo");
    } finally {
      setAvatarBusy(false);
    }
  };

  const onAvatarFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const verdict = validateAvatarFile(file);
    if (!verdict.ok) {
      setError(verdict.message);
      return;
    }

    void runAvatar(async () =>
      AgentsClient.uploadAvatar(props.agentId!, await readFileAsDataUrl(file)),
    );
  };

  const removeAvatar = () => void runAvatar(() => AgentsClient.deleteAvatar(props.agentId!));

  /**
   * Narrowing the mode also drops what that mode forbids, rather than leaving a
   * stale checkbox on screen for the server to reject: a subagent cannot run a
   * side chat, and a primary agent's own subagent list is unaffected — it is
   * *being* someone's subagent that `primary` rules out, which is a rejection
   * the save has to report because it lives on other agents' rows.
   */
  const setMode = (mode: ManagedAgentMode) => {
    setForm((prev) => ({
      ...prev,
      mode,
      subagentIds: mode === "coordinator" ? [] : prev.subagentIds,
      sideChatEnabled: canRunSideChat(mode) ? prev.sideChatEnabled : false,
    }));
  };

  const setProvider = (provider: ProviderKind) => {
    setForm((prev) => {
      const model = resolveModelForProviderChange(prev.model, provider);
      return {
        ...prev,
        provider,
        model,
        tools: filterToolsForProvider(prev.tools, provider),
        ...resolveTraitsForModelChange(prev, { provider, model }),
      };
    });
  };

  /**
   * Changing the model narrows the traits the same way changing the provider
   * does — the ladders differ per model, not only per provider (Haiku has none
   * at all), so a save left alone here would carry an effort the new model never
   * runs.
   */
  const setModel = (model: string) => {
    setForm((prev) => ({
      ...prev,
      model,
      ...resolveTraitsForModelChange(prev, { provider: prev.provider, model }),
    }));
  };

  const effortChoices = agentEffortChoices(form);

  const toggleTool = (toolId: string) => {
    setForm((prev) => ({
      ...prev,
      tools: prev.tools.includes(toolId)
        ? prev.tools.filter((id) => id !== toolId)
        : [...prev.tools, toolId],
    }));
  };

  const toggleTeam = (teamId: TeamId) => {
    setForm((prev) => ({
      ...prev,
      teamIds: prev.teamIds.includes(teamId)
        ? prev.teamIds.filter((id) => id !== teamId)
        : [...prev.teamIds, teamId],
    }));
  };

  const toggleSubagent = (agentId: ManagedAgentId) => {
    setForm((prev) => ({
      ...prev,
      subagentIds: prev.subagentIds.includes(agentId)
        ? prev.subagentIds.filter((id) => id !== agentId)
        : [...prev.subagentIds, agentId],
    }));
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    const validation = validateAgentName(form.name);
    if (!validation.ok) {
      setError(validation.error ?? "Invalid name");
      return;
    }
    if (form.model.trim().length === 0) {
      setError("Model is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await props.onSubmit(form);
      props.onDone();
    } catch (err) {
      // Server errors (e.g. 409 name_conflict / subagent_cycle) already carry
      // a human-readable message — see ManagedAgentError in the server.
      setError(err instanceof Error ? err.message : "Failed to save agent");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title={props.title} onClose={props.onClose} wide>
      <form onSubmit={submit} className="flex max-h-[75vh] flex-col gap-3 overflow-y-auto pr-1">
        <Field label="Name">
          <Input
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            required
            autoFocus
          />
        </Field>

        <Field label="Description (optional)">
          <Input
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          />
        </Field>

        <Field label="Photo">
          <div className="flex items-center gap-3">
            <AgentAvatar
              agentId={props.agentId ?? "new-agent"}
              displayName={form.name}
              avatarFileName={avatarFileName}
              size="lg"
            />
            {props.agentId === null ? (
              <span className="text-xs text-muted-foreground">
                Save the agent first to upload a photo.
              </span>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={avatarFileInputRef}
                  type="file"
                  accept={AVATAR_FILE_INPUT_ACCEPT}
                  className="hidden"
                  onChange={onAvatarFilePicked}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={avatarBusy}
                  onClick={() => avatarFileInputRef.current?.click()}
                >
                  {avatarBusy ? "Working…" : avatarFileName ? "Change photo" : "Upload photo"}
                </Button>
                {avatarFileName ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={avatarBusy}
                    onClick={removeAvatar}
                  >
                    Remove photo
                  </Button>
                ) : null}
              </div>
            )}
          </div>
        </Field>

        <Field label="Mode">
          <div className="flex flex-col gap-1">
            <select
              value={form.mode}
              onChange={(e) => setMode(e.target.value as ManagedAgentMode)}
              className="w-fit rounded border border-border/60 bg-background px-2 py-1 text-xs"
            >
              {MANAGED_AGENT_MODE_OPTIONS.map((mode) => (
                <option key={mode} value={mode}>
                  {MANAGED_AGENT_MODE_LABELS[mode]}
                </option>
              ))}
            </select>
            <span className="text-xs text-muted-foreground">
              {describeManagedAgentMode(form.mode)}
            </span>
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Provider">
            <select
              value={form.provider}
              onChange={(e) => setProvider(e.target.value as ProviderKind)}
              className="rounded border border-border/60 bg-background px-2 py-1 text-xs"
            >
              {PROVIDER_OPTIONS.map((provider) => (
                <option key={provider} value={provider}>
                  {PROVIDER_DISPLAY_NAMES[provider]}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Model">
            <div className="flex gap-2">
              <select
                value={modelOptions.some((m) => m.slug === form.model) ? form.model : ""}
                onChange={(e) => {
                  if (e.target.value) setModel(e.target.value);
                }}
                className="rounded border border-border/60 bg-background px-2 py-1 text-xs"
              >
                <option value="">Custom…</option>
                {modelOptions.map((model) => (
                  <option key={model.slug} value={model.slug}>
                    {model.name}
                  </option>
                ))}
              </select>
              <Input
                value={form.model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="model slug"
                className="flex-1"
              />
            </div>
          </Field>
        </div>

        <Field label="Reasoning effort">
          {effortChoices.levels.length === 0 && !effortChoices.supportsThinkingToggle ? (
            <p className="text-xs text-muted-foreground">
              This model is not in the catalog, so there is no effort ladder to offer. Turns run at
              whatever the provider defaults to.
            </p>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              {effortChoices.levels.length > 0 ? (
                <select
                  value={form.effort}
                  onChange={(e) => setForm((prev) => ({ ...prev, effort: e.target.value }))}
                  className="rounded border border-border/60 bg-background px-2 py-1 text-xs"
                >
                  <option value="">
                    Model default
                    {effortChoices.defaultEffort ? ` (${effortChoices.defaultEffort})` : ""}
                  </option>
                  {effortChoices.levels.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              ) : null}
              {effortChoices.supportsFastMode ? (
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={form.fastMode}
                    onChange={(e) => setForm((prev) => ({ ...prev, fastMode: e.target.checked }))}
                  />
                  Fast mode
                </label>
              ) : null}
              {effortChoices.supportsThinkingToggle ? (
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={form.thinking}
                    onChange={(e) => setForm((prev) => ({ ...prev, thinking: e.target.checked }))}
                  />
                  Thinking
                </label>
              ) : null}
            </div>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            Imposed on every turn this agent runs. Whoever picks it in the composer cannot change
            it.
          </p>
        </Field>

        <Field label="Instructions">
          <Textarea
            value={form.instructions}
            onChange={(e) => setForm((prev) => ({ ...prev, instructions: e.target.value }))}
            rows={5}
          />
        </Field>

        <Field label="Tools">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {toolSections.map((section) => (
              <div
                key={section.section}
                className="flex flex-col gap-1.5 rounded-md border border-border/40 bg-card/30 p-2"
              >
                <div className="text-xs font-semibold text-muted-foreground">
                  {section.sectionLabel}
                </div>
                {section.options.map((option) => (
                  <label key={option.id} className="flex items-start gap-2 text-xs">
                    <Checkbox
                      checked={form.tools.includes(option.id)}
                      onCheckedChange={() => toggleTool(option.id)}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="font-medium">{option.label}</span>
                      {option.note ? (
                        <span className="block text-muted-foreground">{option.note}</span>
                      ) : null}
                    </span>
                  </label>
                ))}
              </div>
            ))}
          </div>
          {warnsAboutCodexShellForQa(form) ? (
            <p className="mt-2 rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
              {CODEX_QA_SHELL_WARNING}
            </p>
          ) : null}
        </Field>

        <Field label="Teams (empty = visible to everyone)">
          {props.teamsLoadFailed ? (
            <p className="rounded border border-border/40 bg-card/30 px-3 py-2 text-xs text-muted-foreground">
              Could not load the team roster (missing <code>u.view</code>). Current team ids:{" "}
              {form.teamIds.length === 0 ? "none" : form.teamIds.join(", ")}
            </p>
          ) : props.teams.length === 0 ? (
            <p className="text-xs text-muted-foreground">No teams yet.</p>
          ) : (
            <div className="flex max-h-40 flex-col gap-1 overflow-y-auto rounded-md border border-border/40 bg-card/30 p-2">
              {props.teams.map((team) => (
                <label key={team.id} className="flex items-center gap-2 text-xs">
                  <Checkbox
                    checked={form.teamIds.includes(team.id)}
                    onCheckedChange={() => toggleTeam(team.id)}
                  />
                  {team.name}
                </label>
              ))}
            </div>
          )}
        </Field>

        {form.mode === "coordinator" ? (
          <Field label="Subagents">
            <p className="text-xs text-muted-foreground">
              Los coordinadores delegan abriendo hilos con los agentes principales visibles; no usan
              subagentes.
            </p>
          </Field>
        ) : (
          <Field label="Subagents">
            {subagentCandidates.length === 0 ? (
              <p className="text-xs text-muted-foreground">No other agents to delegate to yet.</p>
            ) : (
              <div className="flex max-h-40 flex-col gap-1 overflow-y-auto rounded-md border border-border/40 bg-card/30 p-2">
                {subagentCandidates.map((candidate) => (
                  <label key={candidate.id} className="flex items-center gap-2 text-xs">
                    <Checkbox
                      checked={form.subagentIds.includes(candidate.id)}
                      onCheckedChange={() => toggleSubagent(candidate.id)}
                    />
                    {candidate.name}
                  </label>
                ))}
              </div>
            )}
          </Field>
        )}

        <Field label="Side chat">
          <label className="flex items-start gap-2 text-xs">
            <Checkbox
              checked={form.sideChatEnabled}
              disabled={!sideChatAvailable}
              onCheckedChange={() =>
                setForm((prev) => ({ ...prev, sideChatEnabled: !prev.sideChatEnabled }))
              }
            />
            <span className="text-muted-foreground">
              {sideChatAvailable ? (
                <>
                  Offer this agent when someone starts a side chat over selected text. Team
                  visibility above still applies — a user who cannot see the agent is not offered
                  it.
                </>
              ) : (
                <>
                  Not available for a subagent: a side chat is started by a person picking an agent,
                  and this mode is only reachable through delegation.
                </>
              )}
            </span>
          </label>
        </Field>

        <Field label="Etiquetas">
          <TagPicker
            available={availableTags}
            selected={form.tagIds}
            onChange={(tagIds) => setForm((prev) => ({ ...prev, tagIds }))}
          />
          <label className="mt-2 flex items-start gap-2 text-xs">
            <Checkbox
              checked={form.memoryStrict}
              onCheckedChange={() =>
                setForm((prev) => ({ ...prev, memoryStrict: !prev.memoryStrict }))
              }
            />
            <span className="text-muted-foreground">
              Sólo lo etiquetado. Con esto marcado, este agente ignora las memorias y los comandos
              que no llevan ninguna etiqueta — útil para un agente que no debe cargar el catálogo
              técnico que nadie ha etiquetado todavía.
            </span>
          </label>
        </Field>

        {error ? (
          <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={props.onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={submitting}>
            {submitting ? "Saving…" : props.submitLabel}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}
